import { useEffect } from "react";

/**
 * Whenever a form error banner appears anywhere in the app, scroll it into view so the user
 * immediately sees what went wrong — instead of submitting from the bottom of a long form and
 * then hunting up and down for a message pinned to the top.
 *
 * Form errors across the app render a `[role="alert"]` element (and are conditionally mounted, so
 * they're freshly added to the DOM each time an error is shown — including the "clear then set"
 * pattern on re-submit). We watch for those additions and scroll the newest one into view.
 *
 * Toasts (react-toastify, also `role="alert"`) are excluded — they're position-fixed and already
 * visible, so scrolling to them would be wrong.
 */
const AutoScrollToError = () => {
  useEffect(() => {
    const isToast = (el) => !!el.closest?.('[class*="Toastify"]');

    const reveal = (el) => {
      if (!el || isToast(el)) return false;
      // rAF so layout is settled before we scroll (the node was just inserted).
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: "smooth", block: "nearest" })
      );
      return true;
    };

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue; // elements only
          const alert = node.matches?.('[role="alert"]')
            ? node
            : node.querySelector?.('[role="alert"]');
          if (alert && reveal(alert)) return; // one per batch is enough
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
};

export default AutoScrollToError;
